import type {
  GeoJsonFeatureCollection,
  LatLngBounds,
} from '@gpx-spa/geo-domain';
import { latLngBoundsFromLngLatCoordinates } from '@gpx-spa/geo-domain';
import {
  computeCorridorBuffers,
  decodeGoogleRoutesEncodedPolyline,
  type CorridorBufferPathInput,
} from '@gpx-spa/geo-adapters';
import type { ShipmentRouteBatchRow } from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';
import { PATH_ALTERNATIVE_COLORS } from '@/utils/shipment-map-constants';
import {
  CORRIDOR_BUFFER_ALGORITHM_VERSION,
  CORRIDOR_BUFFER_CACHE_TTL_MS,
} from '@/map/operational/corridor-buffer-cache-constants';
import {
  computeShipmentCorridorInputHash,
  shipmentHasCorridorHashableRoutes,
} from '@/map/operational/shipment-corridor-input-hash';
import { useCorridorBufferCacheStore } from '@/stores/useCorridorBufferCacheStore';

/** Primary corridor color per segment (aligned with {@link shipment-route-segments-geojson}). */
const SEGMENT_PRIMARY_COLORS = [
  '#2563eb',
  '#0d9488',
  '#7c3aed',
  '#c2410c',
  '#be185d',
];

type CorridorPathMeta = {
  segmentIndex: number;
  optIdx: number;
  segId: number;
  opt: { name?: string | null; encoded_polyline: string };
};

/**
 * Corridor polygons via {@link computeCorridorBuffers} (worker when available, same as shipment create).
 * When qualityPercent is below 100, polylines are simplified before buffering (same as shipment create).
 *
 * Uses {@link useCorridorBufferCacheStore} (in-memory + optional IndexedDB adapter) to skip redundant worker/Turf work.
 */
export async function buildShipmentRouteCorridorFeatureCollection(
  segments: ShipmentRouteBatchRow[],
  shipmentId: number,
  options?: { useCache?: boolean; qualityPercent?: number },
): Promise<GeoJsonFeatureCollection> {
  const useCache = options?.useCache !== false;
  const qualityPercent = options?.qualityPercent ?? 100;
  const algorithmVersion = CORRIDOR_BUFFER_ALGORITHM_VERSION;

  if (!shipmentHasCorridorHashableRoutes(segments, shipmentId)) {
    return { type: 'FeatureCollection', features: [] };
  }

  const inputHash = await computeShipmentCorridorInputHash(
    segments,
    shipmentId,
    algorithmVersion,
    qualityPercent,
  );
  const inputHashPrefix = inputHash.slice(0, 12);

  if (useCache) {
    const cache = useCorridorBufferCacheStore();
    const hit = await cache.getCachedFeatureCollection(shipmentId, inputHash);
    if (hit) {
      return hit;
    }
  }

  const forShipment = segments.filter((s) => s.shipment_id === shipmentId);
  const inputs: CorridorBufferPathInput[] = [];
  const meta: CorridorPathMeta[] = [];
  let segmentIndex = 0;

  for (const seg of forShipment) {
    const opts = seg.route_options ?? [];
    const width = seg.corridor_width_meters;
    for (let optIdx = 0; optIdx < opts.length; optIdx++) {
      const opt = opts[optIdx];
      if (!opt?.encoded_polyline || width == null || width <= 0) continue;
      const lngLats = decodeGoogleRoutesEncodedPolyline(opt.encoded_polyline);
      if (lngLats.length < 2) continue;
      const raw = lngLats.map((c) => [c[0], c[1]] as [number, number]);
      inputs.push({
        pathId: `seg-${seg.id}-opt-${optIdx}`,
        raw,
        qualityPercent,
        radiusMeters: width,
      });
      meta.push({ segmentIndex, optIdx, segId: seg.id, opt });
    }
    segmentIndex += 1;
  }

  if (inputs.length === 0) {
    const empty: GeoJsonFeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    };
    return empty;
  }

  const results = await computeCorridorBuffers(inputs);
  const features: GeoJsonFeatureCollection['features'] = [];

  for (let i = 0; i < results.length; i++) {
    const r = results[i]!;
    const m = meta[i]!;
    if (!r.buffer?.geometry) continue;

    const isPrimary = m.optIdx === 0;
    const lineColor = isPrimary
      ? SEGMENT_PRIMARY_COLORS[m.segmentIndex % SEGMENT_PRIMARY_COLORS.length]!
      : PATH_ALTERNATIVE_COLORS[
          (m.optIdx - 1) % PATH_ALTERNATIVE_COLORS.length
        ]!;
    const baseName = m.opt.name?.trim();
    const label = baseName
      ? `${baseName}${isPrimary ? '' : ` · alt ${m.optIdx}`}`
      : isPrimary
        ? `Segment ${m.segId}`
        : `Segment ${m.segId} · option ${m.optIdx + 1}`;

    features.push({
      type: 'Feature',
      geometry: r.buffer.geometry as {
        type: string;
        coordinates: unknown;
      },
      properties: {
        map: {
          fill: lineColor,
          fillOpacity: isPrimary ? 0.14 : 0.11,
          stroke: lineColor,
          strokeOpacity: 0.45,
          strokeWidth: 1,
          popup: `Corridor · ${label}`,
        },
      },
    });
  }

  const fc: GeoJsonFeatureCollection = { type: 'FeatureCollection', features };

  if (useCache) {
    const cache = useCorridorBufferCacheStore();
    await cache.setCachedFeatureCollection(shipmentId, inputHash, fc, {
      algorithmVersion,
      expiresAt: Date.now() + CORRIDOR_BUFFER_CACHE_TTL_MS,
    });
  }

  return fc;
}

export function latLngBoundsFromPolygonFeatureCollection(
  fc: GeoJsonFeatureCollection | null,
): LatLngBounds | null {
  if (!fc?.features.length) return null;
  const coords: [number, number][] = [];
  for (const f of fc.features) {
    const g = f.geometry;
    if (g.type === 'Polygon' && Array.isArray(g.coordinates?.[0])) {
      for (const c of g.coordinates[0] as [number, number][]) {
        if (c.length >= 2) coords.push([c[0], c[1]]);
      }
    }
    if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates?.[0]?.[0])) {
      for (const c of g.coordinates[0][0] as [number, number][]) {
        if (c.length >= 2) coords.push([c[0], c[1]]);
      }
    }
  }
  return latLngBoundsFromLngLatCoordinates(coords);
}
